import React, { useEffect } from "react";
import { Table, Switch, message } from "antd";
import { ColumnsType } from "antd/es/table";
import { baseInfoModel } from "../baseInfoModel";
import { momentDateFormat } from "@src/utils/utils";
import { userService } from "../../user/UserService";
import { rosterService } from "../rosterService";
import moment from "moment";

export const BasicDataTable: React.FC = () => {
  const {
    dataSource, pgCfg,
    selectedRowKeys, selectedData,
    setSelectedRowKeys, setSelectedData,
    loading, loadData, loadDs,
    educationalTypeDs, hukouTypeDs,
    baseTypeDs,
  } = baseInfoModel();

  useEffect(() => {
    loadDs();
    loadData();
  }, []);

  const handleStatusChange = async (checked: boolean, record: any) => {
    const hide = message.loading('正在更新状态...', 0);
    try {
      const { retData } = await rosterService.getDetail({ "id": record.id });
      if (!retData) {
        hide();
        message.error('获取用户信息失败');
        return;
      }

      const params = {
        ...retData,
        id: record.id,
        status: checked ? 0 : 1,
        shops: retData.shops?.map((e: any) => e.code) ?? [],
        roles: retData.roles?.map((e: any) => e.code) ?? [],
      }
      const updateRet = await userService.updateLocal(params);
      hide();
      if (updateRet.success) {
        message.success('状态更新成功');
        loadData();
      }
      else {
        message.error('更新失败!');
      }
    } catch (e: any) {
      hide();
      if (e.response) {
        message.error(e.response.data?.message || '网络请求失败');
      } else if (e.request) {
        message.error('网络连接失败，请检查网络');
      } else {
        message.error(e.message || '操作失败!');
      }
    }
  };

  const basicColumns: ColumnsType<any> = [
    {
      align: 'center',
      title: '序号',
      width: 60,
      render: (v, r, i) => i + 1
    },
    {
      title: '员工工号',
      dataIndex: 'staffId',
      width: 80,
      align: 'center',
    },
    {
      title: '账户',
      dataIndex: 'code',
      width: 100,
      align: 'center',
    },
    {
      title: '姓名',
      dataIndex: 'name',
      width: 100,
      align: 'center',
    },
    {
      title: '性别',
      dataIndex: 'gender',
      width: 60,
      align: 'center',
      render: (value) => value === 1 ? "男" : value === 2 ? "女" : ""
    },
    {
      title: '联系电话',
      dataIndex: 'phoneNumber',
      width: 100,
      align: 'center',
    },
    {
      title: '所属机构',
      dataIndex: 'orgName',
      width: 200,
      align: 'center',
    },
    {
      title: '岗位',
      dataIndex: 'job',
      width: 150,
      align: 'center',
    },
    {
      title: '入职时间',
      dataIndex: 'hireDate',
      width: 100,
      align: 'center',
      render: (value) => momentDateFormat(value),
    },
    {
      title: '学历',
      dataIndex: 'education',
      width: 80,
      align: 'center',
      render: (v: any, r: any, i: any) => {
        const item = educationalTypeDs?.find(item => item.value === v);
        return <span style={{ lineHeight: '28px' }}>{item?.label || v}</span>;
      },
    },
    {
      title: '专业',
      dataIndex: 'major',
      width: 80,
      align: 'center',
    },
    {
      title: '年龄',
      dataIndex: 'age',
      width: 60,
      align: 'center',
      render: (text: string, record: any) => {
        if (!record.birthDate) return '-';
        const age = moment().diff(moment(record.birthDate), 'years');
        return age;
      }
    },
    {
      title: '驾驶证',
      dataIndex: 'certificateName',
      width: 100,
      align: 'center',
    },
    {
      title: '办公基地',
      dataIndex: 'dormitory',
      width: 120,
      align: 'center',
      render: (value: string) => {
        const item = baseTypeDs?.find(item => item.value === value);
        return item?.label || value;
      }
    },
    {
      title: '是否住宿',
      dataIndex: 'accommodation',
      width: 80,
      align: 'center',
      render: (value) => value === "0" ? "否" : value === "1" ? "是" : ""
    },
    {
      title: '毕业院校',
      dataIndex: 'graduationSchool',
      width: 120,
      align: 'center',
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      align: 'center',
      render: (value, record) => (
        <Switch
          checked={value === 0}
          onChange={(checked) => handleStatusChange(checked, record)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
        />
      )
    },
    {
      title: '创建人',
      dataIndex: 'createUser',
      width: 80,
      align: 'center',
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 150,
      align: 'center',
      render: (v: any) => momentDateFormat(v),
    },
  ];


  const onRow = (record: any, selected: boolean) => {
    let arr: any[] = [];
    let list: any[] = [];
    if (selected) {
      arr = Array.from(new Set([record.id]));
      list = Array.from(new Set([record]));
    } else {
      arr = selectedRowKeys.filter(item => { return item !== record.id });
      list = selectedData.filter(item => item.id !== record.id)
    }
    setSelectedRowKeys(arr);
    setSelectedData(list);
  };

  return (
    <Table
      style={{ cursor: "pointer" }}
      loading={loading}
      rowKey={record => record.id as string}
      rowSelection={{
        type: "radio",
        selectedRowKeys: selectedRowKeys,
        onSelect: (record, selected, selectedRows) => onRow(record, selected),
      }}
      onRow={record => ({
        onClick: () => onRow(record, true)
      })}
      columns={basicColumns}
      dataSource={dataSource}
      pagination={{
        showSizeChanger: true,
        ...pgCfg,
        defaultPageSize: 25,
        pageSizeOptions: ['15', '25', '50', '80'],
        position: ["bottomRight"],
        showTotal: () => `共${pgCfg.total}条`,
        onChange: (pageNum, pageSize) => {
          loadData({ pageNum, pageSize }).then();
        }
      }}
      scroll={{ x: "max-content", y: "500px" }}
    />
  );
}